import moment from "moment";

import { ITrialLicense } from "@/base/NewselaGlobalLayoutV2/NavigationBar/PremiumTrial/TrialBanner/TrialBanner.types";
import { NCE_TRIAL_CAMPAIGN_TITLES } from "@/constants/TRIAL_LICENSE_CAMPAIGNS";
import getTrialLicenseFarthestExpirationDate from "@/utils/getTrialLicenseFarthestExpirationDate";
import { ITrialCampaign } from "./PopoutSubscriptions.types";

export const getExpirationDateFor = (
  licenseType: keyof ITrialLicense,
  products: string[],
  licenses: ITrialLicense[],
) => {
  const expirationDate = licenses.find((license) =>
    products.includes(license[licenseType] as string),
  )?.date_ends;
  return expirationDate ? moment(expirationDate).format("MMM DD, YYYY") : null;
};

export const getLicensesLists = (
  hasNceTrialCampaign: boolean,
  userLicenses: ITrialLicense[] = [],
) => {
  let trials: { [key: string]: ITrialLicense | ITrialCampaign } = {};
  const subscriptions: { [key: string]: ITrialLicense } = {};

  for (const license of userLicenses) {
    if (!license.is_active) continue;

    if (!license.is_trial) {
      subscriptions[license.product] = license;
      continue;
    }

    const currentExpiration = moment(license.date_ends);
    const storedExpiration = moment(trials[license.product]?.expirationDate);

    if (!trials[license.product] || currentExpiration > storedExpiration) {
      trials[license.product] = {
        ...license,
        expirationDate: license.date_ends
          ? moment(license.date_ends).format("MMM DD, YYYY")
          : null,
      };
    }
  }

  if (hasNceTrialCampaign) {
    trials = {
      nce_trial_campaign: {
        product: "nce_trial_campaign",
        expirationDate: getExpirationDateFor(
          "campaign",
          NCE_TRIAL_CAMPAIGN_TITLES,
          userLicenses,
        ),
      },
    };
  }

  return [Object.values(trials), Object.values(subscriptions)];
};

export const getDaysLeftInTrial = (
  licenses: ITrialLicense[],
  isNCETrial: boolean,
) => {
  const trialLicenses = isNCETrial
    ? licenses.filter((license) =>
        NCE_TRIAL_CAMPAIGN_TITLES.includes(license.campaign),
      )
    : licenses;
  const { daysToExpiration } =
    getTrialLicenseFarthestExpirationDate(trialLicenses);
  return daysToExpiration;
};

export const productTextMap: { [key: string]: string } = {
  collections: "Collections",
  ela: "Newsela ELA",
  science: "Newsela Science",
  stem: "Newsela STEM",
  everwrite: "Newsela Writing",
  social_studies: "Newsela Social Studies",
  essentials: "Newsela Essentials",
  power_words: "Power Words",
  teacher_learning_hub: "PL Pass",
  nce_trial_campaign: "Newsela Certified Educator Trial",
};

export const getSubscriptionsInformation = (
  licenses: Array<ITrialLicense | ITrialCampaign> | undefined,
) => {
  const hasStem = licenses?.some((item) => item.product === "stem");
  const hasScience = licenses?.some((item) => item.product === "science");
  const hasBothSTEMAndScience = hasStem && hasScience;

  return licenses?.reduce(
    (array, license) => {
      if (productTextMap[license.product]) {
        // Don't display Science if a user also has STEM license.
        if (hasBothSTEMAndScience && license.product === "science") {
          return array;
        }
        array.push({
          product: productTextMap[license.product] || "",
          expirationDate:
            "expirationDate" in license
              ? license?.expirationDate
              : license?.date_ends,
        });
      }
      return array;
    },
    [] as Array<{ product: string; expirationDate: string | null }>,
  );
};
